import { FC, useEffect, useMemo, useState } from "react";
import orderAddSummaryHelper from "./common/order-add-summary.helper";
import OrderAddSummaryRouteComponent from "./route/order-add-summary-route.component";
import orderAddSummaryFactory from "./common/order-add-summary.factory";
import orderAddSummaryMapFactory from "./route/order-add-summary-map-markers.factory";
import OrderAddSuccessModalComponent from "../add-success-modal/order-add-success-modal.component";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import CardComponent from "../../../../../../common/components/card/card.component";
import FormFieldComponent from "../../../../../../common/components/form/field/form-field.component";
import Column from "../../../../../../common/components/grid/column";
import Row from "../../../../../../common/components/grid/row";
import MapComponent from "../../../../../../common/components/map/map.component";
import MapMarker from "../../../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../../../common/components/map/types/map-route";
import SearchRoadRoutingRequest from "../../../../../../common/utils/search-road-route/search-road-routing.request";
import SearchRoadRoutingResponse, {
  SearchRoadRoutingResponseRouteLeg,
} from "../../../../../../common/utils/search-road-route/search-road-routing.response";
import orderTranslationsHelper from "../../../../../../languages/order-translations.helper";
import OrderAddPassengerListItem from "../../types/order-add-passenger-list-item";
import orderAddFactory from "../../factory/order-add.factory";
import OrderAddTaxiCorporation from "../../types/order-add-taxi-corporation";
import OrderAddRouteItem from "../../types/order-add-route-waypoint";
import orderAddValidationService from "../../validation/order-add-validation.service";
import OrderAddDispatcher from "../../types/order-add-dispatcher";
import OrderAddPassenger from "../../types/order-add-passenger";
import searchRoadRoutingService from "../../../../../../common/utils/search-road-route/search-road-routing.service";
import orderService from "../../../../../../common/services/order/order.service";
import OrderAddParams from "../../../../../../common/services/order/add/order-add-params";
import useAbort from "../../../../../../common/hooks/use-abort";
import OrderAddError from "../../../../../../common/services/order/add/order-add-error";
import useRideSolved from "../../../../../../common/services/ride/solved/use-ride-solved";
import RideSolvedParams from "../../../../../../common/services/ride/solved/ride-solved-params";

type OrderAddSummaryProps = {
  passengerList: OrderAddPassengerListItem[];
  selectedTaxiCorporation: OrderAddTaxiCorporation | null;
  routes: OrderAddRouteItem[];
  orderNumber: string;
  selectedDispatcherUuid: OrderAddDispatcher["uuid"] | null;
  selectedPassengerUuid: OrderAddPassenger["uuid"] | null;
  restoreForm: () => void;
  validateForm: () => boolean;
  setValidationMessage: (value: string[]) => void;
  setIsReturnRideActive: (isReturnRideActive: string[]) => void;
};

const OrderAddSummaryComponent: FC<OrderAddSummaryProps> = (props) => {
  const translations = orderTranslationsHelper.getAddTranslations().summary;

  const [isAddOrderFetching, setIsAddOrderFetching] = useState(false);

  const [mapRoutes, setMapRoutes] = useState<MapRoute[]>([]);

  const [isOrderAddSuccessModalOpen, setIsOrderAddSuccessModalOpen] =
    useState(false);

  const orderAddAbort = useAbort();

  const [addedOrderUuid, setAddedOrderUuid] = useState("");

  const [responseRouteLegs, setResponseRouteLegs] = useState<
    SearchRoadRoutingResponseRouteLeg[]
  >([]);

  const rideSolved = useRideSolved();
  const rideSolvedAbort = useAbort();

  const selectedPassengers = props.passengerList.map(
    (listItem) => listItem.passenger,
  );

  const fetchSolvedRides = () => {
    const params: RideSolvedParams = orderAddFactory.createSolvedRidesRequest(
      props.selectedTaxiCorporation!,
      props.routes,
    );

    rideSolved.load(params, rideSolvedAbort.signal);
  };

  const waypoints = useMemo(
    () =>
      orderAddSummaryFactory.createSummaryRouteWaypoints(
        props.routes,
        responseRouteLegs,
        rideSolved.data.seq,
      ),
    [rideSolved.data],
  );

  const validateRoutesForSolvedOrderFetch = () => {
    const validationErrors =
      orderAddValidationService.validateRoutesForSolvedOrderFetch(
        props.passengerList,
        props.routes,
      );

    return !validationErrors.length;
  };

  useEffect(() => {
    if (
      !props.selectedTaxiCorporation ||
      !props.routes.length ||
      !validateRoutesForSolvedOrderFetch()
    ) {
      rideSolved.reset();
      return;
    }

    fetchSolvedRides();

    return rideSolvedAbort.revoke;
  }, [JSON.stringify(props.routes), props.selectedTaxiCorporation]);

  const mapMarkers: MapMarker[] =
    orderAddSummaryMapFactory.createMapMarkers(waypoints);

  const fetchRoute = () => {
    const waypointsCouldBeConnected =
      orderAddSummaryHelper.getWaypointGroupsCouldBeConnected(waypoints);

    const coordinatesOfWaypointsCouldBeConnected =
      orderAddSummaryHelper.getCoordinatesOfWaypointsCouldBeConnected(
        waypointsCouldBeConnected,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    coordinatesOfWaypointsCouldBeConnected.forEach((coordinates) => {
      const request: SearchRoadRoutingRequest = {
        waypointCoordinates: coordinates,
        excludeHighway:
          !props.selectedTaxiCorporation?.contractPermitsTollRoads,
      };

      const fetch = searchRoadRoutingService.search(request);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];

      responses.forEach((response) => {
        const mapRoute = response.routes[0]
          ? orderAddSummaryMapFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }
      });

      const newMapRoutes: MapRoute[] = mapRouteWaypointGroups.map(
        (routeWaypointGroup) => ({
          waypoints: routeWaypointGroup,
        }),
      );

      setMapRoutes(newMapRoutes);

      const responseRouteLegs = responses
        .map((response) => response.routes.map((route) => route.legs).flat())
        .flat();

      setResponseRouteLegs(responseRouteLegs);
    });
  };

  useEffect(() => {
    if (!waypoints.length) {
      return;
    }

    fetchRoute();
  }, [JSON.stringify(waypoints)]);

  const handleOrderAddRequest = (orderUuid: string) => {
    setIsOrderAddSuccessModalOpen(true);
    setAddedOrderUuid(orderUuid);
    props.setIsReturnRideActive([]);
  };

  const onApiError = (error: OrderAddError) => {
    props.setValidationMessage([error.message]);
  };

  const onAddOrderSuccessNextOrderButtonClick = () => {
    props.restoreForm();
    setIsOrderAddSuccessModalOpen(false);
    setMapRoutes([]);
  };

  const onAddOrderSuccessCloseButtonClick = () => {
    props.restoreForm();
    setIsOrderAddSuccessModalOpen(false);
    setMapRoutes([]);
  };

  const onSubmitButtonClick = () => {
    const isFormValid = props.validateForm();

    if (!isFormValid) {
      return;
    }

    const params: OrderAddParams = orderAddFactory.createAddRequestParams(
      props.orderNumber,
      props.selectedTaxiCorporation!,
      props.routes,
      props.selectedDispatcherUuid ?? undefined,
    );

    setIsAddOrderFetching(true);

    orderService
      .addOrder(params, orderAddAbort.signal)
      .then(handleOrderAddRequest)
      .catch(onApiError)
      .finally(() => {
        setIsAddOrderFetching(false);
      });
  };

  return (
    <>
      <CardComponent
        header={{ title: translations.headingText }}
        classNames={{ root: "order_add_summary" }}
      >
        <div className="order_add_summary__map_wrapper">
          <MapComponent
            markers={mapMarkers}
            autoFit
            autoFitOnUpdate
            routes={mapRoutes}
          />
        </div>
        <FormFieldComponent label={translations.taxiLabel}>
          <span data-test-id="summary-taxi">
            {orderAddSummaryHelper.getTaxiCorporationLabel(
              props.selectedTaxiCorporation,
            )}
          </span>
        </FormFieldComponent>
        <FormFieldComponent label={translations.passengerListLabel}>
          <span data-test-id="summary-passengers">
            {orderAddSummaryHelper.getPassengersListLabel(selectedPassengers)}
          </span>
        </FormFieldComponent>
        <FormFieldComponent label={translations.routeLabel}>
          <OrderAddSummaryRouteComponent
            waypoints={waypoints}
            selectedPassengerUuid={props.selectedPassengerUuid}
          />
        </FormFieldComponent>
        <Row>
          <Column>
            <FormFieldComponent
              label={translations.orderNumberLabel}
              classNames={{
                content: "order_add_summary__order_number_form_field_content",
              }}
            >
              {orderAddSummaryHelper.getOrderNumberLabel(props.orderNumber)}
            </FormFieldComponent>
          </Column>
        </Row>
        <ButtonComponent
          onClick={onSubmitButtonClick}
          type="primary"
          classNames={{ root: "w-100" }}
          isLoading={isAddOrderFetching}
          idForTesting="order-add-submit-button"
        >
          {translations.submitButtonText}
        </ButtonComponent>
      </CardComponent>
      {isOrderAddSuccessModalOpen && (
        <OrderAddSuccessModalComponent
          isOpen={isOrderAddSuccessModalOpen}
          addedOrderUuid={addedOrderUuid}
          onCloseClick={onAddOrderSuccessCloseButtonClick}
          onAddNextOrderButtonClick={onAddOrderSuccessNextOrderButtonClick}
        />
      )}
    </>
  );
};

export default OrderAddSummaryComponent;
